import { Injectable } from "@angular/core";
import { TalentProfile } from "../models/portfolio.interface";

export interface ProfileCompleteness {
  overallScore: number;
  sections: {
    basicInfo: {
      score: number;
      total: number;
      completed: string[];
      missing: string[];
    };
    resume: {
      score: number;
      total: number;
      completed: string[];
      missing: string[];
    };
    workSamples: {
      score: number;
      total: number;
      completed: string[];
      missing: string[];
    };
    projectDetails: {
      score: number;
      total: number;
      completed: string[];
      missing: string[];
    };
  };
  ranking: number; // 1-100 scale
  completionPercentage: number;
  recommendations: string[];
}

@Injectable({
  providedIn: "root",
})
export class ProfileCompletenessService {
  constructor() {}

  /**
   * Calculate comprehensive profile completeness and ranking
   */
  calculateProfileCompleteness(profile: TalentProfile): ProfileCompleteness {
    const basicInfo = this.assessBasicInfo(profile);
    const resume = this.assessResume(profile);
    const workSamples = this.assessWorkSamples(profile);
    const projectDetails = this.assessProjectDetails(profile);

    // Calculate weighted overall score
    const overallScore = Math.round(
      basicInfo.score * 0.3 +
        resume.score * 0.2 +
        workSamples.score * 0.4 +
        projectDetails.score * 0.1
    );

    const completionPercentage = Math.round(
      (overallScore /
        (basicInfo.total +
          resume.total +
          workSamples.total +
          projectDetails.total)) *
        100
    );

    const ranking = this.calculateRanking(profile, overallScore);
    const recommendations = this.generateRecommendations(
      basicInfo,
      resume,
      workSamples,
      projectDetails
    );

    return {
      overallScore,
      sections: { basicInfo, resume, workSamples, projectDetails },
      ranking,
      completionPercentage,
      recommendations,
    };
  }

  /**
   * Assess basic profile information completeness
   */
  private assessBasicInfo(profile: TalentProfile): {
    score: number;
    total: number;
    completed: string[];
    missing: string[];
  } {
    const checks = [
      { field: "firstName", label: "First Name", value: profile.first_name },
      { field: "lastName", label: "Last Name", value: profile.last_name },
      { field: "jobTitle", label: "Job Title", value: profile.display_name },
      { field: "location", label: "Location", value: profile.location },
      {
        field: "experience",
        label: "Years of Experience",
        value: profile.experience_years,
      },
      {
        field: "skills",
        label: "Skills (min 3)",
        value: (profile.skills?.length || 0) >= 3,
      },
      { field: "workStatus", label: "Work Status", value: profile.work_status },

      { field: "website", label: "Portfolio Website", value: profile.website },
    ];

    const completed = checks
      .filter((check) => this.isFieldComplete(check.value))
      .map((c) => c.label);
    const missing = checks
      .filter((check) => !this.isFieldComplete(check.value))
      .map((c) => c.label);

    return {
      score: completed.length,
      total: checks.length,
      completed,
      missing,
    };
  }

  /**
   * Assess resume completeness
   */
  private assessResume(profile: TalentProfile): {
    score: number;
    total: number;
    completed: string[];
    missing: string[];
  } {
    const checks = [
      {
        field: "resumeUploaded",
        label: "Resume Uploaded",
        value: profile.resume_url,
      },
      {
        field: "resumeProcessed",
        label: "Resume Processed",
        value: profile.resume_text,
      },
      { field: "education", label: "Education Info", value: profile.education },
    ];

    const completed = checks
      .filter((check) => this.isFieldComplete(check.value))
      .map((c) => c.label);
    const missing = checks
      .filter((check) => !this.isFieldComplete(check.value))
      .map((c) => c.label);

    return {
      score: completed.length,
      total: checks.length,
      completed,
      missing,
    };
  }

  /**
   * Assess work samples completeness
   */
  private assessWorkSamples(profile: TalentProfile): {
    score: number;
    total: number;
    completed: string[];
    missing: string[];
  } {
    const projectCount = profile.portfolio_projects?.length || 0;
    const hasImages =
      profile.portfolio_projects?.some((p) => p.media && p.media.length > 0) ||
      false;
    const hasDescriptions =
      profile.portfolio_projects?.some((p) => p.description) || false;
    const hasProjectTypes =
      profile.portfolio_projects?.some((p) => p.project_type) || false;

    const checks = [
      {
        field: "optimalProjects",
        label: "3+ Projects (Recommended)",
        value: projectCount >= 3,
      },
      {
        field: "projectImages",
        label: "Project Images/Media",
        value: hasImages,
      },
      {
        field: "projectDescriptions",
        label: "Project Descriptions",
        value: hasDescriptions,
      },
      {
        field: "projectTypes",
        label: "Project Categories",
        value: hasProjectTypes,
      },
    ];

    const completed = checks.filter((check) => check.value).map((c) => c.label);
    const missing = checks.filter((check) => !check.value).map((c) => c.label);

    return {
      score: completed.length,
      total: checks.length,
      completed,
      missing,
    };
  }

  /**
   * Assess project details completeness
   */
  private assessProjectDetails(profile: TalentProfile): {
    score: number;
    total: number;
    completed: string[];
    missing: string[];
  } {
    const hasLiveUrls =
      profile.portfolio_projects?.some((p) => p.live_site_url) || false;
    const hasCaseStudies =
      profile.portfolio_projects?.some((p) => p.case_study_url) || false;
    const hasToolsUsed =
      profile.portfolio_projects?.some(
        (p) => (p.tools_used?.length || 0) > 0
      ) || false;

    const checks = [
      { field: "liveUrls", label: "Live Project URLs", value: hasLiveUrls },
      {
        field: "caseStudies",
        label: "Case Study Links",
        value: hasCaseStudies,
      },
      {
        field: "toolsUsed",
        label: "Tools & Technologies",
        value: hasToolsUsed,
      },
    ];

    const completed = checks.filter((check) => check.value).map((c) => c.label);
    const missing = checks.filter((check) => !check.value).map((c) => c.label);

    return {
      score: completed.length,
      total: checks.length,
      completed,
      missing,
    };
  }

  /**
   * Calculate profile ranking (1-100 scale)
   */
  private calculateRanking(
    profile: TalentProfile,
    completenessScore: number
  ): number {
    let ranking = 0;

    // Base score from completeness (0-60 points)
    ranking += Math.round(completenessScore * 3);

    // Experience bonus (0-15 points)
    const experience = profile.experience_years || 0;
    if (experience >= 10) ranking += 15;
    else if (experience >= 7) ranking += 12;
    else if (experience >= 4) ranking += 8;
    else if (experience >= 2) ranking += 5;

    // Portfolio quality bonus (0-15 points)
    const projectCount = profile.portfolio_projects?.length || 0;
    const hasHighQualityMedia =
      profile.portfolio_projects?.some((p) => p.media && p.media.length >= 2) ||
      false;
    const hasDetailedProjects =
      profile.portfolio_projects?.some(
        (p) => p.description && p.description.length > 50
      ) || false;

    if (projectCount >= 5 && hasHighQualityMedia && hasDetailedProjects)
      ranking += 15;
    else if (projectCount >= 3 && hasHighQualityMedia) ranking += 10;

    // Skills bonus (0-10 points)
    const skillCount = profile.skills?.length || 0;
    if (skillCount >= 8) ranking += 10;
    else if (skillCount >= 5) ranking += 7;
    else if (skillCount >= 3) ranking += 4;

    // Ensure ranking is between 1-100
    return Math.max(1, Math.min(100, ranking));
  }

  /**
   * Generate personalized recommendations
   */
  private generateRecommendations(
    basicInfo: any,
    resume: any,
    workSamples: any,
    projectDetails: any
  ): string[] {
    const recommendations: string[] = [];

    // Basic info recommendations
    if (basicInfo.missing.includes("Portfolio Website")) {
      recommendations.push(
        "Include your portfolio website URL to showcase more of your work"
      );
    }
    if (basicInfo.missing.includes("Skills (min 3)")) {
      recommendations.push(
        "Add at least 3 relevant skills to help companies find you"
      );
    }

    // Work samples recommendations
    if (workSamples.missing.includes("3+ Projects (Recommended)")) {
      recommendations.push(
        "Add more projects to strengthen your portfolio (3-5 recommended)"
      );
    }
    if (workSamples.missing.includes("Project Descriptions")) {
      recommendations.push(
        "Add descriptions to your projects to provide context"
      );
    }

    // Project details recommendations
    if (projectDetails.missing.includes("Case Study Links")) {
      recommendations.push(
        "Include case study links to showcase your design process"
      );
    }
    if (projectDetails.missing.includes("Live Project URLs")) {
      recommendations.push(
        "Add live project URLs so companies can see your work in action"
      );
    }

    // Default recommendation if profile is mostly complete
    if (recommendations.length === 0) {
      recommendations.push(
        "Your profile looks great! Consider adding more projects or case studies to stand out even more."
      );
    }

    return recommendations.slice(0, 3); // Limit to top 3 recommendations
  }

  /**
   * Check if a field is considered complete
   */
  private isFieldComplete(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "number") return value > 0;
    if (typeof value === "boolean") return value;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return false;
  }

  /**
   * Get completion status badge info
   */
  getCompletionBadge(completionPercentage: number): {
    text: string;
    color: string;
    bgColor: string;
  } {
    if (completionPercentage >= 90) {
      return {
        text: "Excellent",
        color: "text-green-800",
        bgColor: "bg-green-100",
      };
    } else if (completionPercentage >= 75) {
      return { text: "Good", color: "text-blue-800", bgColor: "bg-blue-100" };
    } else if (completionPercentage >= 50) {
      return {
        text: "Fair",
        color: "text-yellow-800",
        bgColor: "bg-yellow-100",
      };
    } else {
      return {
        text: "Needs Work",
        color: "text-red-800",
        bgColor: "bg-red-100",
      };
    }
  }

  /**
   * Get ranking badge info
   */
  getRankingBadge(ranking: number): {
    text: string;
    color: string;
    bgColor: string;
  } {
    if (ranking >= 85) {
      return {
        text: "Top Talent",
        color: "text-purple-800",
        bgColor: "bg-purple-100",
      };
    } else if (ranking >= 70) {
      return {
        text: "Strong Profile",
        color: "text-green-800",
        bgColor: "bg-green-100",
      };
    } else if (ranking >= 50) {
      return {
        text: "Developing",
        color: "text-blue-800",
        bgColor: "bg-blue-100",
      };
    } else {
      return {
        text: "Getting Started",
        color: "text-gray-800",
        bgColor: "bg-gray-100",
      };
    }
  }
}
